import {Component,EventEmitter,Input,OnInit,Output,ViewChild} from '@angular/core';
import {LieuOD} from "@domain/lieu/lieuod";
import {TypeLieu} from "@domain/lieu/typeLieu";
import {MatLegacyInput as MatInput} from "@angular/material/legacy-input";
import {TranslateService} from "@ngx-translate/core";
import {LieuProfil} from "@domain/lieu/lieuProfil";
import {Lieu} from "@domain/lieu/lieu";
import {Observable} from "rxjs";
import {LieuSelectionComponent} from "./selection/lieu-selection.component";
import {MatLegacyDialog as MatDialog} from "@angular/material/legacy-dialog";
import {LieuParam} from "@domain/common/lieu/lieu-param";
import {Adresse} from "@domain/profil/adresse";

/**
 * Affichage du composant lieu
 * Il est nécessaire de lui fournir un paramétrage via LieuParam
 */
@Component({
    selector: 'lieu',
    host: {'data-test-id': 'lieu'},
    templateUrl: './lieu.component.html',
    exportAs: 'lieuComponent'
})
export class LieuComponent implements OnInit {
    /** Paramétrage du composant lieu */
    @Input() lieuParam: LieuParam;

    /** Type de lieu */
    @Input() typeLieu: TypeLieu;
    @Output() typeLieuChange = new EventEmitter<TypeLieu>();

    /** Lieu */
    @Input() lieu: LieuProfil | LieuOD | Adresse;
    @Output() lieuChange = new EventEmitter<LieuProfil | LieuOD>();

    /** Champ */
    @ViewChild('input') input: MatInput;

    /** Composant désactivé ou non */
    @Input() disabled: boolean;

    /** Valeur obligatoire ou non */
    @Input() required: boolean;

    /** Fonction de suppression de la valeur */
    @Input() onClear: () => void;

    /** Affichage d'un tooltip en fin de champ indiquant le type de lieu */
    @Input() isShowTypeLieuTooltip: boolean = false;

    /** Tooltip indiquant le type de lieu (aucun pour le type "Autre") */
    get typeLieuTooltip(): string {
        let libelle: string;

        switch (this.typeLieu) {
            case TypeLieu.TYPE_LIEU_RESIDENCE_FAMILIALE:
                libelle = 'lieu.typeLieu.residenceFamiliale';
                break;
            case TypeLieu.TYPE_LIEU_RESIDENCE_ADMINISTRATIVE:
                libelle = 'lieu.typeLieu.residenceAdministrative';
                break;
            case TypeLieu.TYPE_LIEU_ETABLISSEMENT:
                libelle = 'lieu.typeLieu.etablissement';
                break;
            default:
                return null;
        }

        return this.translateService.instant(libelle);
    }

    /** Placeholder du champ "Adresse" différent suivant les cas d'usage */
    get placeholder(): string {
        //Si on a le paramétrage de placeholder
        if (this.lieuParam?.placeHolder) {
            //On récupère le libellé
            const libelle = this.lieuParam.placeHolder(this.lieu?.adresse as string,this.disabled);
            if (libelle != null && libelle != "") {
                return this.translateService.instant(libelle);
            }
        }

        return '';
    }

    /**
     * Constructeur
     */
    constructor(private translateService: TranslateService,
                private matDialog: MatDialog) {}

    /**
     * Hook initilisation
     */
    ngOnInit(): void {

        if (this.lieuParam.libelleInputFormatOnInit != null && this.lieu != null) {
            this.lieu.adresse = this.lieuParam.libelleInputFormatOnInit(this.lieu);
        }
    }

    /**
     * Sélection d'une adresse
     *
     * @param $event événement utilisateur provenant du clic sur le la valeur
     */
    showPopupChoixLieu($event?: Event): void {
        //Si la source de l'action provient d'un événement utilisateur on stop la propagation de l'événement
        if ($event) {
            $event.stopPropagation();
        }

        //Composant désactivé : pas de raison d'ouvrir la popup
        if (this.disabled) {
            return;
        }

        //Ouverture de la popup
        this.openPopupChoixLieu(this.typeLieu,this.lieu as Lieu | LieuOD).subscribe({
            next: data => {
                //Vérification du résultat
                if (data) {
                    //Mise à jour de l'objet et du type...
                    //Uniquement pour les lieux OD/OT : cela permet de revenir sur l'onglet "saisie d'adresse". Dans les autres cas ce n'est pas ce qu'on veut.
                    if (data.type && this.lieuParam.isRevenirOngletSaisieAdresseAfterSelect) {
                        this.typeLieu = data.type;
                    }

                    //Si on a pas de mapper
                    if (this.lieuParam.mapper == null) {
                        //On utilise le mapping historique
                        this.lieu = {
                            ...this.lieu,
                            idLocalisation: data.objet?.idAdresse ?? data.objet?.id,
                            adresse: data.adresse,
                            libelleEtablissement: data.objet?.libelle,
                            rue: data.objet?.rue,
                            codePostal: data.objet?.codePostal,
                            ville: data.objet?.ville,
                            pays: data.objet?.pays,
                            codePays: data.objet?.codePays3 || data.objet?.codePays
                        };
                    } else {
                        //Sinon on utilise le mapper du paramétrage
                        this.lieu = this.lieuParam.mapper(data);
                    }

                    //Si on a une méthode pour définir le formatage du libellé de l'input
                    if (this.lieuParam.libelleInputFormatOnSelect != null) {
                        this.lieu.adresse = this.lieuParam.libelleInputFormatOnSelect(data);
                    }

                    //... et propagation
                    this.typeLieuChange.emit(this.typeLieu);
                    this.lieuChange.emit(this.lieu as LieuProfil | LieuOD);
                }
            }
        });
    }

    /**
     * Affichage de la popup de sélection d'une adresse
     *
     * @param typeLieu Type de lieu
     * @param lieu L'objet LieuOD à compléter après sélection d'une adresse
     */
    private openPopupChoixLieu(typeLieu: number,lieu: Lieu | LieuOD): Observable<any> {
        //Affichage du chainage
        return this.matDialog.open(LieuSelectionComponent,{
            data: {
                isSaisieAutorisee: this.lieuParam.isSaisieAutorisee(),
                typeLieu,
                lieu,
                lieuParam: this.lieuParam,
            },
            minWidth: 1000
        }).afterClosed();
    }

    /**
     * Vidage du champ
     *
     * @param $event Évènement (clic)
     */
    clear($event: Event) {
        $event.preventDefault();
        $event.stopPropagation();

        this.onClear();
    }
}
